import React, { useState } from "react";
import { useItems } from "@/hooks/useItems";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useForm } from "react-hook-form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Plus, MoreVertical, Search, Pencil, Trash2, CheckCircle, Info } from "lucide-react";
import type { Item } from "@/types/item";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { countries } from "@/constants/countryCodes";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { newItemSchema } from "@/schema/schema";
import LoadingIndicator from "@/components/LoadingIndicator";
import { Label } from "@/components/ui/label";
import { taxTypes } from "@/constants/taxTypes";
import { classificationCodes } from "@/constants/classificationCodes";
import { UomCombobox } from "@/components/uom-combobox";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const ItemForm = ({
  initialData = null,
  onSubmit,
}: {
  initialData?: Item | null;
  onSubmit: (data: Item) => void;
}) => {
  const { format: formatDecimal, step } = useDecimalPrecision();
  const [taxAmountEditable, setTaxAmountEditable] = useState(true)
  const form = useForm<Item>({
    resolver:zodResolver(newItemSchema),
    defaultValues: initialData || {
      taxType: "",
      taxRate: 0,
      taxAmount: 0,
      discountRate: 0,
      feeChargeRate: 0,
      classification:"",
      description: "",
      countryOfOrigin: "",
      itemCode: "",
      isEditable:0,
      measurement: "",
      // Inventory Management Fields
      quantity: 0,
      weightedAverage: null,
      salesGlCode: null,
      purchaseGlCode: null,
      stockType: 'stock'
    },
  });

  const baseAmount = form.watch('unitPrice') || 0;
  const taxRate = form.watch('taxRate') || 0;

  return (
    <div className="space-y-6 pt-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          
          <FormField
            control={form.control}
            name="itemCode"
            render={({ field }) => (
              <FormItem>
                <Label required>
                  Item Code
                </Label>
                <FormControl>
                  <Input {...field} className="h-8" />
                </FormControl>
                <FormMessage />
                <FormDescription className="text-sm text-muted-foreground">
                  Internal item code is a unique identifier for tracking
                  products.
                </FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="classification"
            render={({ field }) => (
              <FormItem>
                <Label required>
                  Classification
                </Label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select classification" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="w-80">
                    {classificationCodes.map((classification) => (
                      <SelectItem
                        key={classification.Code}
                        value={classification.Code}
                      >
                        {classification.Code} - {classification.Description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <Label>
                  Description
                </Label>
                <FormControl>
                  <Input {...field} className="h-10" />
                </FormControl>
                <FormDescription className="text-sm text-muted-foreground">
                  Maximum 200 characters
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unitPrice"
            render={({ field }) => (
              <FormItem>
                <Label required>
                  Unit Price
                </Label>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    step={step}
                    className="h-10"
                    onChange={(e) => {
                      const newUnitPrice = e.target.valueAsNumber;
                      field.onChange(newUnitPrice);
          
                      // Recalculate taxAmount
                      if (taxAmountEditable) {
                        const newTaxAmount = (newUnitPrice * taxRate) / 100;
                        form.setValue("taxAmount", parseFloat(formatDecimal(newTaxAmount)));
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="taxType"
              render={({ field }) => (
                <FormItem>
                  <Label required>
                    Tax Type
                  </Label>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select tax type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {taxTypes.map((taxType) => (
                        <SelectItem key={taxType.Code} value={taxType.Code}>
                          {taxType.Description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taxRate"
              render={({ field }) => (
                <FormItem>
                  <Label>
                    Tax Rate (%)
                  </Label>
                  <FormControl>
                    <Input
                      disabled={!taxAmountEditable || form.getValues("isEditable") == 1}
                      {...field}
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      className="h-10"
                      onChange={(e) => {
                        const newTaxRate = e.target.valueAsNumber;
                        field.onChange(newTaxRate);
            
                        // auto update taxAmount
                        const newTaxAmount = (baseAmount * newTaxRate) / 100;
                        form.setValue("taxAmount", parseFloat(formatDecimal(newTaxAmount)));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taxAmount"
              render={({ field }) => (
                <FormItem>
                  <Label>
                    Tax Amount
                  </Label>
                  <FormControl>
                    <Input
                      disabled={taxAmountEditable}
                      {...field}
                      type="number"
                      min="0"
                      step={step}
                      className="h-10"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="discountRate"
              render={({ field }) => (
                <FormItem>
                  <Label>
                    Discount Rate (%)
                  </Label>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className="h-10"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="feeChargeRate"
              render={({ field }) => (
                <FormItem>
                  <Label>
                    Fee/Charge Rate (%)
                  </Label>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      className="h-10"
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="countryOfOrigin"
            render={({ field }) => (
              <FormItem>
                <Label required>Country Of Origin</Label>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="-- Select Country --" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.Code} value={country.Code}>
                        {country.Country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="measurement"
            render={({ field }) => (
              <FormItem>
                <Label>Unit Of Measurement (UOM)</Label>
                {/* Searches the full LHDN unit list by code or name. Only the
                    code is stored — it is all LHDN accepts. */}
                <FormControl>
                  <UomCombobox
                    value={field.value}
                    onChange={(code) => field.onChange(code)}
                    className="h-10 w-full"
                    placeholder="-- Select UOM --"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />


          {/* Stock & Inventory Section */}
          <div className="pt-6 border-t">
            <h3 className="text-lg font-semibold mb-4">Stock & Inventory</h3>

            <FormField
              control={form.control}
              name="stockType"
              render={({ field }) => (
                <FormItem>
                  <Label required>
                    Stock Type
                  </Label>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Select stock type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="stock">Stock</SelectItem>
                      <SelectItem value="non-stock">Non-Stock</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <Label>
                      Quantity
                    </Label>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step={step}
                        className="h-10 bg-gray-100 dark:bg-gray-800"
                        disabled
                        value={field.value ?? 0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="weightedAverage"
                render={({ field }) => (
                  <FormItem>
                    <Label>
                      Weighted Average
                    </Label>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        step={step}
                        className="h-10 bg-gray-100 dark:bg-gray-800"
                        disabled
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="salesGlCode"
                render={({ field }) => (
                  <FormItem>
                    <Label>
                      Sales GL Code
                    </Label>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        className="h-10"
                        placeholder="e.g., 40001 or ACC-001"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      />
                    </FormControl>
                    <FormDescription className="text-sm text-muted-foreground">
                      GL code for sales transactions 
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="purchaseGlCode"
                render={({ field }) => (
                  <FormItem>
                    <Label>
                      Purchase GL Code
                    </Label>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        className="h-10"
                        placeholder="e.g., 50001 or PUR-001"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : e.target.value)}
                      />
                    </FormControl>
                    <FormDescription className="text-sm text-muted-foreground">
                      GL code for purchase transactions
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

        <FormField
          control={form.control}
          name="isEditable"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label>
                  Tax Amount Editable
                </Label>
                <FormDescription>
                  Indicates whether the tax amount can be manually adjusted.
                </FormDescription>
              </div>
              <FormControl>
                <Checkbox
                  checked={field.value === 1}
                  onCheckedChange={(checked) => {
                    setTaxAmountEditable(!checked);
                    field.onChange(checked ? 1 : 0)
                    form.setValue("taxRate", 0);
                  }}
                  value={field.value}
                />
              </FormControl>
            </FormItem>
          )}
        />

          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              className="flex-1 h-14 bg-[#0F172A] text-base font-semibold"
            >
              {initialData ? "Update Item" : "Register Item"}
            </Button>
            <SheetClose asChild>
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-14 text-base font-semibold"
              >
                Cancel
              </Button>
            </SheetClose>
          </div>
        </form>
      </Form>
    </div>
  );
};

// Separate the search functionality into a custom hook
const useItemSearch = (items: Item[] | undefined) => {
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredItems = React.useMemo(() => {
    if (!Array.isArray(items)) { return [] };
    return items.filter((item) =>
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [items, searchTerm]);

  return { searchTerm, setSearchTerm, filteredItems };
};

export const ItemList = () => {
  const { formatAmount } = useDecimalPrecision();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    items,
    createItemAsync,
    updateItem,
    deleteItemAsync,
    isLoading,
    error,
  } = useItems();


  const {
    settings,
    isLoading: isLoadingSettings,
    updateSettings,
    isUpdating,
  } = useCompanySettings();

  const [selectedItem, setSelectedItem] = React.useState<Item | null>(null);
  const [isSheetOpen, setIsSheetOpen] = React.useState(false);
  const [showToggleDialog, setShowToggleDialog] = React.useState(false);
  const [pendingToggleValue, setPendingToggleValue] = React.useState(false);
  const { searchTerm, setSearchTerm, filteredItems } = useItemSearch(
    items?.data
  );

  // Handle loading and error states
  if (isLoading) return <LoadingIndicator/>;
  if (error) return <div>Error: {error.message}</div>;
  if (!items?.data) return <div>No items found</div>;

  const handleSubmit = async (data: Partial<Item>) => {
    let success = false;
    let toastTitle = "";
    let toastMessage = "";
    data.unitPrice = data.unitPrice ? data.unitPrice : 0;
    data.taxRate = data.taxRate ?? undefined;
    data.classification = data.classification ?? "";

    try {
      if (selectedItem) {
        const updatedData = { ...data, id: selectedItem?.id }
        await updateItem(updatedData, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["items"] });
            success = true;
            toastTitle = "Update Successful"
            toastMessage = "The item has been successfully updated."
          },
          onError: (error: any) => {
            toast({
              title: "Error updating item",
              description: error?.response?.data?.message || error?.message || "Failed to update item. Please try again.",
            })
          }
        });
      } else {
        await createItemAsync(data as Omit<Item, "id">, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["items"] });
            success = true;
            toastTitle = "Item Created"
            toastMessage = "The item has been successfully created."
          },
          onError: (error: any) => {
            toast({
              title: "Error creating item",
              variant: "destructive",
              description: error?.response?.data?.message || error?.message || "Failed to create item. Please try again.",
            })
          }
        });
      }
    } catch (err) {
      console.error("Operation failed:", err);
    } finally {
      setIsSheetOpen(false);
      if (success) {
        setSelectedItem(null);
        cleanupBodyLock();
        toast({
          title: toastTitle,
          description: toastMessage,
          duration: 5000,
          variant: "default",
          icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
        });
      }
    }
  };

  const cleanupBodyLock = () => {
    document.body.style.removeProperty('pointer-events');
    document.body.removeAttribute('data-scroll-locked');
  }

  const handleEdit = (item: Item) => {
    setSelectedItem(item);
    setIsSheetOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteItemAsync(id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["items"] });
           toast({
                title: "Success",
                description: "Item has been deleted.",
                duration: 5000,
                icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
              });
        },
      });
    } catch (err) {
      console.error("Delete operation failed:", err);
    }
  };

  const handleInvoiceLevelToggle = (checked: boolean) => {
    // If turning OFF, show confirmation dialog
    if (!checked) {
      setPendingToggleValue(checked);
      setShowToggleDialog(true);
      return;
    }

    // If turning ON, apply immediately
    confirmToggle(checked);
  };

  const confirmToggle = async (checked: boolean) => {
    try {
      await updateSettings({
        enableInvoiceLevelRecalculation: checked,
      });
      toast({
        title: "Settings Updated",
        description: `Invoice Level Recalculation is now ${checked ? "ON" : "OFF"}`,
        duration: 3000,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-400" />,
      });
      setShowToggleDialog(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || error?.message || "Failed to update settings",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4 p-4">
      {/* Invoice Level Recalculation Toggle */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
        <div className="flex items-center gap-2">
          <Label className="font-semibold text-base">
            Invoice Level Recalculation
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <div className="space-y-2">
                  <p className="font-semibold">Invoice Level Recalculation</p>
                  <div className="space-y-1 text-sm">
                    <p><strong>ON (Default):</strong> Weighted average recalculates immediately when editing/deleting backdated stock transactions. Ensures real-time accuracy but may impact performance with large transaction volumes.</p>
                    <p><strong>OFF:</strong> Defers weighted average recalculation to Month End Stock process. Improves performance but weighted average may be temporarily inaccurate until month-end processing.</p>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Badge variant={settings?.enableInvoiceLevelRecalculation ? "default" : "secondary"}>
            {settings?.enableInvoiceLevelRecalculation ? "ON" : "OFF"}
          </Badge>
        </div>
        <Switch
          checked={settings?.enableInvoiceLevelRecalculation ?? true}
          onCheckedChange={handleInvoiceLevelToggle}
          disabled={isUpdating || isLoadingSettings}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 w-[300px] shadow-sm"
            />
          </div>
        </div>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <Button onClick={() => setSelectedItem(null)}>
              <Plus className="mr-2 h-4 w-4" />
              Register Item
            </Button>
          </SheetTrigger>
          <SheetContent className="w-[800px] sm:w-[800px] overflow-y-auto"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            cleanupBodyLock();
          }}>
            <SheetHeader>
              <SheetTitle>
                {selectedItem ? "Edit Item" : "Register New Item"}
              </SheetTitle>
              <SheetDescription>
                {selectedItem
                  ? "Edit the item details below."
                  : "Fill in the item details below."}
              </SheetDescription>
            </SheetHeader>
            <ItemForm initialData={selectedItem} onSubmit={handleSubmit} />
          </SheetContent>
        </Sheet>
      </div>

      <Table>
        <TableHeader className="bg-slate-50 dark:bg-slate-800">
          <TableRow>
            <TableHead>Item Code</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Classification</TableHead>
            <TableHead>Unit Price</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead>Stock Type</TableHead>
            <TableHead>Tax Type</TableHead>
            <TableHead>Country</TableHead>
            <TableHead className="w-[50px]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredItems.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{item.itemCode}</TableCell>
              <TableCell>{item.description}</TableCell>
              <TableCell>{item.classification}</TableCell>
              <TableCell>
                RM {formatAmount(item.unitPrice)}
              </TableCell>
              <TableCell>
                {formatAmount(item.quantity ?? 0)}
              </TableCell>
              <TableCell>
                <Badge variant={item.stockType === 'stock' ? 'default' : 'secondary'}>
                  {item.stockType === 'stock' ? 'Stock' : 'Non-Stock'}
                </Badge>
              </TableCell>
              <TableCell>{item.taxType}</TableCell>
              <TableCell>{item.countryOfOrigin}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEdit(item)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Invoice Level Recalculation Confirmation Dialog */}
      <AlertDialog open={showToggleDialog} onOpenChange={setShowToggleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn OFF Invoice Level Recalculation?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                You are about to disable Invoice Level Recalculation. This will affect how weighted averages are calculated.
              </p>

              <div className="space-y-3 text-sm">
                <div className="border rounded-lg p-3 bg-orange-50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-900/50">
                  <p className="font-semibold text-orange-900 dark:text-orange-300 mb-1">When OFF:</p>
                  <ul className="list-disc pl-5 space-y-1 text-orange-800 dark:text-orange-400">
                    <li>Weighted average will NOT recalculate immediately when editing/deleting backdated stock transactions</li>
                    <li>Weighted averages may be temporarily inaccurate until month-end processing</li>
                    <li>All recalculations will be deferred to Month End Stock process</li>
                    <li>Better performance with large transaction volumes</li>
                  </ul>
                </div>

                <div className="border rounded-lg p-3 bg-blue-50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900/50">
                  <p className="font-semibold text-blue-900 dark:text-blue-300 mb-1">When ON (Default):</p>
                  <ul className="list-disc pl-5 space-y-1 text-blue-800 dark:text-blue-400">
                    <li>Weighted average recalculates immediately on edit/delete operations</li>
                    <li>Real-time accuracy for all stock valuations</li>
                    <li>May impact performance with large transaction volumes</li>
                  </ul>
                </div>
              </div>

              <p className="text-sm font-semibold text-muted-foreground">
                Are you sure you want to turn OFF Invoice Level Recalculation?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowToggleDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmToggle(pendingToggleValue)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Yes, Turn OFF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
